import styled from "styled-components";
import { Tab } from './index';
import { ChangeEvent, IFFM, IRack, TransferCargo, TransferTruck } from "../../../../globals/interfaces";
import apiClient from "../../../../apiClient";
import useSWR from "swr";
import useUser from "../../../../customHooks/useUser";
import { useRef, useState } from "react";
import moment from "moment";
import { FormGroup } from "reactstrap";
import { Label } from "reactstrap";
import { Input } from "reactstrap";
import VirtualTable from "../../../custom/VirtualTable";
import { extractReactTableData, fileDownload, getQrCodeUrl, print } from "../../../../utils";
import ActionIcon from "../../../custom/ActionIcon";
import GenerateTruckManifest from "./GenerateTruckManifest";
import useStationInfo from "../../../reporting/breakdownProgress/useStationInfo";
import _ from "lodash";

export type LogData = TransferCargo & IRack & TransferTruck;

export default function Log ({
    activeTab
}: {
    activeTab: Tab;
}) {

    if (activeTab !== 'LOG') return null;

    return (
        <Content />
    );
}

const fetcher = async (key: string): Promise<Array<IFFM>> => {
    try {
        const res = await apiClient.get(key);
        return res.data;
    } catch (err) {
        alert(err);
        return [];
    }
}

const Content = ({

}) => {

    const { user } = useUser();
    const stationInfo = useStationInfo();
    const s_destination = user.s_unit.substring(1, 4);
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [flightId, setFlightId] = useState();
    const apiEndpoint = (moment(date).isValid() && s_destination) ?
        `/buildup/log?date=${date}&s_destination=${s_destination}` : 
        null;

    const {
        data
    } = useSWR(apiEndpoint, fetcher, {
        fallbackData: []
    });

    const uniqueflightIds = Array.from(new Set(data.map(item => item.s_flight_id)));
    const flightIds = uniqueflightIds.map(item => ({ s_flight_id: item }));
    const tableData = data.filter(item => item.s_flight_id === flightId);
    const tableRef = useRef();

    const printPdf = async () => {
        const s_flight_id = _.get(tableData, '[0].s_flight_id', '');
        const qrCodeUrl = await getQrCodeUrl(s_flight_id);
        print(<GenerateTruckManifest 
            data={tableData} 
            stationInfo={stationInfo} 
            qrCodeUrl={qrCodeUrl}
        />)
    }

    const exportData = () => {
        const schema = [{
            key: 's_flight_id',
            name: 'Flight'
        }, {
            key: 's_flight_uld',
            name: 'ULD'
        }, {
            key: 's_mawb',
            name: 'MAWB',
            mawb: true
        }, {
            key: 'loadedBy',
            name: 'Warehouse Ownership'
        }, {
            key: 'approvedBy',
            name: 'Office Ownership'
        }, {
            key: 'dockDoor',
            name: 'Door #'
        }, {
            key: 'name',
            name: 'Trailer'
        }, {
            key: 'modifiedBy',
            name: 'Last Modified by',
            email: true
        }, {
            key: 'modifiedAt',
            name: 'Last Modified at',
            datetime: true
        }];
        
        const data = extractReactTableData(tableRef, [], schema);
        fileDownload(data, `Truck Buildup Data ${flightId} ${moment().format('YYYY-MM-DD')}.csv`);
    }

    return (
        <Container>
            <FilterContainer>
                <FormGroup>
                    <Label>Date</Label>
                    <Input 
                        type={'date'} 
                        value={date} 
                        onChange={(e: ChangeEvent) => setDate(e.target.value)}
                    />
                    <VirtualTable 
                        data={flightIds}
                        mapping={[{
                            name: 'Flight',
                            value: 's_flight_id'
                        }]}
                        handleClick={item => setFlightId(item.s_flight_id)}
                        numRows={10}
                    />
                </FormGroup>
            </FilterContainer>
            <TableContainer>
                {
                    flightId && (
                        <div>
                            <FlexContainer>
                                <h3>{flightId}</h3>
                                <FlexContainer>
                                    <ActionIcon 
                                        type="print"
                                        onClick={printPdf}
                                        className="mr-2"
                                    />
                                    <ActionIcon 
                                        type="download"
                                        onClick={exportData}
                                    />
                                </FlexContainer>
                            </FlexContainer>
                            <VirtualTable 
                                data={tableData}
                                mapping={[{
                                    name: 'ULD',
                                    value: 's_flight_uld'
                                }, {
                                    name: 'MAWB',
                                    value: 's_mawb',
                                    s_mawb: true
                                }, {
                                    name: 'Warehouse Ownership',
                                    value: 'loadedBy',
                                    email: true
                                }, {
                                    name: 'Office Ownership',
                                    value: 'approvedBy',
                                    email: true
                                }, {
                                    name: 'Door #',
                                    value: 'dockDoor',
                                    number: true
                                }, {
                                    name: 'Trailer',
                                    value: 'name'
                                }, {
                                    name: 'Last Modified by',
                                    value: 'modifiedBy',
                                    email: true
                                }, {
                                    name: 'Last Modified at',
                                    value: 'modifiedAt',
                                    datetime: true,
                                    utc: true
                                }]}
                                numRows={10}
                                reactTableRef={tableRef}
                            />
                        </div>
                    )
                }
            </TableContainer>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    gap: 10px;
`;

const FilterContainer = styled.div`
    flex: 1;
`;

const TableContainer = styled.div`
    flex: 3;
`;

const FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;